// src/pages/EditDealerSupportNeeds.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface Report {
  id: number;
  created_at: string;
}

interface DealerSupportNeedsData {
  challenges: string;
  actionPlan: string;
}

const EditDealerSupportNeeds: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // Dealer id from route
  const navigate = useNavigate();
  
  // Form state for dealer support needs.
  const [formData, setFormData] = useState<DealerSupportNeedsData>({
    challenges: "",
    actionPlan: ""
  });
  
  // State to store the record id (if one exists) and report integration.
  const [recordId, setRecordId] = useState<number | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [newestReportId, setNewestReportId] = useState<number | null>(null);

  // Step 1: Fetch dealer reports.
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then((res) => res.json())
      .then((data: Report[]) => {
        if (Array.isArray(data) && data.length > 0) {
          // Assume reports are sorted descending by created_at.
          setReports(data);
          setNewestReportId(data[0].id);
        } else {
          // No report exists; automatically create one.
          fetch(`/api/dealers/${id}/reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" }
          })
            .then((res) => res.json())
            .then((newReport) => setNewestReportId(newReport.id))
            .catch((err) => console.error("Error creating report", err));
        }
      })
      .catch((err) => console.error("Error fetching reports", err));
  }, [id]);

  // Step 2: Once newestReportId is available, fetch dealer support needs using the report‑centric endpoint.
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/dealer-support-needs`)
      .then((res) => res.json())
      .then((data) => {
        setFormData({
          challenges: data.challenges || "",
          actionPlan: data.action_plan || ""
        });
        if (data.id) {
          setRecordId(data.id);
        }
      })
      .catch((err) => console.error("Error fetching dealer support needs", err));
  }, [newestReportId, id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Step 3: On form submission, update or create the record using the report‑centric endpoint.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      dealer_id: parseInt(id!, 10),
      challenges: formData.challenges,
      actionPlan: formData.actionPlan
    };

    if (newestReportId) {
      if (recordId) {
        // Update existing record.
        fetch(`/api/reports/${newestReportId}/dealer-support-needs`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: recordId, ...payload })
        })
          .then(() => navigate(`/dealer/${id}`))
          .catch((err) =>
            console.error("Error updating dealer support needs", err)
          );
      } else {
        // Create a new record.
        fetch(`/api/reports/${newestReportId}/dealer-support-needs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })
          .then(() => navigate(`/dealer/${id}`))
          .catch((err) =>
            console.error("Error creating dealer support needs", err)
          );
      }
    } else {
      console.error("No report found for this dealer.");
    }
  };

  // UI styling remains the same.
  const containerStyle: React.CSSProperties = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" };
  const cardStyle: React.CSSProperties = { backgroundColor: "#FFFFFF", borderRadius: "8px", boxShadow: "0 2px 5px rgba(0,0,0,0.1)", maxWidth: "800px", margin: "30px auto", padding: "30px" };
  const headerStyle: React.CSSProperties = { textAlign: "center", color: "#e41e26", borderBottom: "2px solid #e41e26", paddingBottom: "10px", marginBottom: "20px", fontSize: "24px" };
  const labelStyle: React.CSSProperties = { display: "block", marginBottom: "5px", fontWeight: "bold" };
  const inputStyle: React.CSSProperties = { width: "100%", padding: "8px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc" };
  const buttonStyle: React.CSSProperties = { padding: "5px 10px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "10px" };

  return (
    <div style={{ backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" }}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Dealer Support Needs</h1>
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Challenges:</label>
          <textarea
            name="challenges"
            style={inputStyle}
            value={formData.challenges}
            onChange={handleChange}
            placeholder="List challenges"
          />
          <label style={labelStyle}>Action Plan:</label>
          <textarea
            name="actionPlan"
            style={inputStyle}
            value={formData.actionPlan}
            onChange={handleChange}
            placeholder="Enter action plan"
          />
          <div style={{ marginTop: "20px" }}>
            <button type="submit" style={buttonStyle}>Save</button>
            <button type="button" style={buttonStyle} onClick={() => navigate(`/dealer/${id}`)}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDealerSupportNeeds;
